import React from 'react';
import PropTypes from 'prop-types';
import { Box, Card, CardContent, Typography, Button, Alert } from '@mui/material';
import { Icon } from '@iconify/react';
import { Link as RouterLink } from 'react-router-dom';

function EmailConflictError({ email, onTryAgain }) {
  return (
    <Card 
      sx={{ 
        maxWidth: 600, 
        mx: 'auto', 
        mt: 4,
        borderRadius: 3,
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
        border: '2px solid',
        borderColor: 'error.light'
      }}
    >
      <CardContent sx={{ p: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              backgroundColor: 'error.lighter',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2
            }}
          >
            <Icon 
              icon="mdi:email-alert" 
              width={40} 
              height={40} 
              style={{ color: '#f44336' }}
            />
          </Box>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: 'error.main' }}>
            Email Already in Use
          </Typography>
        </Box>

        <Alert 
          severity="error" 
          icon={<Icon icon="mdi:information" />}
          sx={{ 
            mb: 3,
            borderRadius: 2,
            '& .MuiAlert-message': {
              width: '100%'
            }
          }}
        >
          <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
            Registration Not Allowed
          </Typography>
          <Typography variant="body2">
            You must use another email for registration since you have used this email to apply as an applicant.
          </Typography>
        </Alert>

        <Box sx={{ backgroundColor: 'grey.100', p: 2, borderRadius: 2, mb: 3 }}>
          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
            Email Address:
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 600, wordBreak: 'break-all' }}>
            {email}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Button
            fullWidth
            size="large"
            variant="contained"
            onClick={onTryAgain}
            startIcon={<Icon icon="mdi:arrow-left" />}
            sx={{
              backgroundColor: '#00AB55',
              '&:hover': {
                backgroundColor: '#007a3d'
              },
              textTransform: 'none',
              fontWeight: 600,
              py: 1.5
            }}
          >
            Try with different email
          </Button>
          
          <Button
            fullWidth
            size="large"
            variant="outlined"
            component={RouterLink}
            to="/main"
            startIcon={<Icon icon="mdi:login" />}
            sx={{
              borderColor: '#00AB55',
              color: '#00AB55',
              '&:hover': {
                borderColor: '#007a3d',
                backgroundColor: 'rgba(0, 171, 85, 0.04)'
              },
              textTransform: 'none',
              fontWeight: 600,
              py: 1.5
            }}
          >
            Login to existing account
          </Button>
        </Box>

        <Box sx={{ mt: 3, p: 2, backgroundColor: 'info.lighter', borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <Icon icon="mdi:lightbulb-outline" width={20} style={{ color: '#1976d2', marginTop: 2 }} />
            <Box>
              <Typography variant="caption" color="info.dark" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                Why is this happening?
              </Typography>
              <Typography variant="caption" color="text.secondary">
                This email is already registered in our system as an applicant. To maintain account security, 
                each role (Applicant, Judge, etc.) requires a unique email address.
              </Typography>
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

EmailConflictError.propTypes = {
  email: PropTypes.string.isRequired,
  onTryAgain: PropTypes.func.isRequired
};

export default EmailConflictError;

